import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, ArrowRight, BookOpen, CheckCircle2, ChevronRight, FileCheck2, FileSpreadsheet,
  FileUp, Files, Info, Inbox, Link2, Loader2, Pencil, Send,
} from 'lucide-react';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { useAuth } from '../contexts/AuthContext';
import { useDateFilter } from '../contexts/DateFilterContext';
import { useToast } from '../components/Toast';
import { validateFiles } from '../lib/fileValidation';
import AdminDashboard from './AdminDashboard';
import Bookkeeping from './Bookkeeping';
import WorkflowWarningModal from '../components/WorkflowWarningModal';
import GapReasonModal, { type GapReasonCode } from '../components/GapReasonModal';
import StatementDrillModal from '../components/StatementDrillModal';
import EncryptedPdfModal from '../components/EncryptedPdfModal';
import { exportPlXlsx } from '../lib/export-pl-xlsx';
import {
  bsExportRows, exportStatementExcel, safeFilePart, trialExportRows,
} from '../lib/statement-export';
import { workflowWarningDef, type WorkflowWarningKind } from '../lib/workflow-warnings';
import { useActiveOcrJobs } from '../lib/useActiveOcrJobs';
import {
  uploadWorkflowDocument,
  WorkflowEncryptedPdfError,
  type WorkflowUploadChannel,
  type WorkflowUploadResult,
} from '../lib/workflow-upload';

// Document Processing home (2026-10-22 spec §5; renamed from "Monthly Close
// Workflow" 2026-10-28) — the 5-step wizard:
// 1 bank statements · 2 invoices · 3 link & reconcile · 4 summary ·
// 5 financial statement. Structure and labels port from the interactive
// mockup workflow-home-demo.html, rendered with the app's Tailwind idioms
// and tr() three-way strings.
//
// This task ships the shell + S3 (matching entry, read-only — the matching
// engine stays on /bank-statements), S4 (summary: discrepancies + temp lists)
// and S5 (financial statements embed + temp banner + voucher drill + Excel).

// ── /workflow/summary response shape (api/src/routes/workflow.ts) ──────────

interface WorkflowStatement {
  id: string; account_number: string; statement_year: number; statement_month: number;
  currency: string; status: string; review_flags: string[]; file_id: string;
}
interface WorkflowInvoice {
  id: string; invoice_number: string; counterparty: string; total: number;
  direction: string; needs_review_flags: string[];
}
interface WorkflowGap { gap_number: string; status: string; reason_code: string | null; note: string | null }
interface WorkflowWarning { kind: string; step: number; target_id: string; target_type: string }
// Temp legs (api/src/routes/workflow.ts tempTxs/toLeg): bank tx amounts are
// SIGNED (deposit − withdrawal; payment legs negative) — display Math.abs().
interface WorkflowTempTx { id: string; date: string; description: string; amount: number }
interface WorkflowTempInvoice { id: string; invoice_number: string; counterparty: string; total: number }
interface WorkflowTempLeg { bank_txs: WorkflowTempTx[]; invoices: WorkflowTempInvoice[] }
interface WorkflowSummary {
  step1: { statements: WorkflowStatement[]; total_statements?: number; flagged_statements?: number };
  step2: { invoices: WorkflowInvoice[]; gaps: WorkflowGap[] };
  step3: { unmatched_bank_txs: number; open_invoices: number };
  temp: { receipt: WorkflowTempLeg; payment: WorkflowTempLeg; receipt_total: number; payment_total: number };
  warnings: WorkflowWarning[];
  degraded?: string[];
}

const isContinuityFlag = (f: string) => f.startsWith('continuity_');

// Amounts render absolute (controller ruling: temp bank-tx amounts are signed,
// payment legs negative) in the mockup's en-HK 2-dp format.
const fmtAmt = (n: number) => Math.abs(n).toLocaleString('en-HK', { minimumFractionDigits: 2 });
// ISO date (YYYY-MM-DD from D1) → '30 Apr 2026'; raw string when unparseable.
const fmtDate = (d: string) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const gapReasonLabel = (code: string | null): string => {
  switch (code) {
    case 'voided': return tr('Voided', '已作廢', '已作废');
    case 'cancelled': return tr('Cancelled', '已取消', '已取消');
    case 'not_issued': return tr('Not yet issued', '未開出', '未开出');
    case 'other': return tr('Other', '其他', '其他');
    default: return code || '';
  }
};

// ── Small building blocks (mockup check() / dropzone / progress) ───────────

function CheckCard({ icon: Icon, warn, pending, title, desc, onClick, extra }: {
  icon: React.ComponentType<{ className?: string }>;
  warn: boolean;
  /** Upload/OCR still in flight — show a neutral "checking…" pill instead of
   *  a premature OK (the checks can only be judged once import completes). */
  pending?: boolean;
  title: string;
  desc: React.ReactNode;
  onClick?: () => void;
  extra?: React.ReactNode;
}) {
  return (
    <div
      onClick={warn ? onClick : undefined}
      className={`border rounded-lg p-3.5 flex items-start gap-3 ${
        warn
          ? 'bg-amber-50/60 border-amber-200 cursor-pointer hover:shadow-sm'
          : 'bg-card'
      }`}
    >
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${warn ? 'text-amber-500' : 'text-green-600'}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
          {title}
          {pending
            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-semibold animate-pulse">{tr('checking…', '檢查中…', '检查中…')}</span>
            : warn
              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">{tr('⚠ REVIEW', '⚠ 覆核', '⚠ 复核')}</span>
              : <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold">OK</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
      {extra && <div className="text-xs mt-1 shrink-0" onClick={e => e.stopPropagation()}>{extra}</div>}
      {warn && <ChevronRight className="h-4 w-4 text-muted-foreground mt-1.5 shrink-0" />}
    </div>
  );
}

function DropZone({ icon: Icon, title, hint, busy, onFile }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint: string;
  busy: boolean;
  onFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const disabled = busy;
  return (
    <div
      onClick={() => { if (!disabled) inputRef.current?.click(); }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f && !disabled) onFile(f);
      }}
      className={`border-2 border-dashed rounded-lg p-8 text-center transition ${
        disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-muted/50'
      } ${dragOver ? 'border-primary bg-muted/50' : ''}`}
    >
      <input
        ref={inputRef} type="file" className="hidden"
        accept="application/pdf,image/*"
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
      />
      <Icon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
  );
}

function UploadProgress({ label }: { label: string }) {
  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1.5">
        <Loader2 className="h-4 w-4 animate-spin" /> {label}
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '60%' }} />
      </div>
    </div>
  );
}

// One row of the S4 Temp Receipt / Temp Payment tables (mockup trow):
// muted date, free text, right-aligned abs amount.
function TempRow({ date, children, amount }: { date: string | null; children: React.ReactNode; amount: number }) {
  return (
    <tr className="border-t last:border-0">
      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{date ? fmtDate(date) : '—'}</td>
      <td className="px-3 py-2">{children}</td>
      <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{fmtAmt(amount)}</td>
    </tr>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function Workflow({ initialStep = 1 }: { initialStep?: number }) {
  useTranslation(); // subscribe to language changes so tr() updates
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // 1-based step; ?step= overrides the initialStep prop.
  const stepParam = Number(searchParams.get('step'));
  const step = Number.isInteger(stepParam) && stepParam >= 1 && stepParam <= 5 ? stepParam : initialStep;
  const goStep = (n: number) => setSearchParams({ step: String(n) });

  const { data, error } = useQuery({
    queryKey: ['workflow-summary'],
    queryFn: () => api('/workflow/summary') as Promise<WorkflowSummary>,
    refetchInterval: 15000,
  });

  const [busyChannel, setBusyChannel] = useState<WorkflowUploadChannel | null>(null);
  const [lastUpload, setLastUpload] = useState<{ channel: WorkflowUploadChannel; fileName: string; result: WorkflowUploadResult } | null>(null);
  const [warnModal, setWarnModal] = useState<{ kind: string; targetId?: string; detail?: string } | null>(null);
  const [gapModal, setGapModal] = useState<string | null>(null); // gap number
  // Encrypted upload → in-place password prompt (same modal as File Storage /
  // Upload Document; try-decrypt re-imports server-side).
  const [encryptedPdf, setEncryptedPdf] = useState<{ fileId: string; fileName: string } | null>(null);

  // Server-side upload truth. busyChannel is component-local and dies on any
  // remount — crossing the lg breakpoint (maximize ↔ resizable swaps the
  // mobile/desktop shells in Layout) or navigating away and back unmounts this
  // page and used to reset the upload UI to a blank slate. active-jobs is
  // polled from the server, so the in-flight state survives every remount.
  const { jobs: activeJobs } = useActiveOcrJobs();
  const uploadInFlight = busyChannel !== null || activeJobs.length > 0;
  const activeJobImporting = activeJobs.some(j => j.status === 'importing');

  // S5 (Task 12) — statements embed + voucher drill. The FY selector drives
  // the embedded Bookkeeping (useDateFilter) automatically; exports and the
  // drill read the same start/end so everything stays FY-scoped.
  const { selectedFY, startDate, endDate } = useDateFilter();
  const [fsTab, setFsTab] = useState<'pl' | 'bs' | 'trial'>('pl');
  const [drillOpen, setDrillOpen] = useState(false);
  const [fsExporting, setFsExporting] = useState(false);

  // Admins keep their existing landing page (check copied from Dashboard.tsx).
  if (user?.role === 'admin') return <AdminDashboard />;

  // ── derived state ──
  const statements = data?.step1?.statements || [];
  const invoices = data?.step2?.invoices || [];
  const gaps = data?.step2?.gaps || [];
  const openGaps = gaps.filter(g => g.status === 'open');

  // Step 1 checklist sources
  const continuityStmt = statements.find(s => s.review_flags.some(isContinuityFlag));
  const ocrFlagStmt = statements.find(s => s.review_flags.includes('ocr_failed') || s.review_flags.includes('balance_mismatch'));
  const needsReviewStmt = statements.find(s => s.status === 'needs_review' && !s.review_flags.some(isContinuityFlag));
  const ocrStmtSource = ocrFlagStmt ?? needsReviewStmt;
  const bankUpload = lastUpload && lastUpload.channel === 'bank_statement' ? lastUpload : null;
  const ocrWarn = !!ocrStmtSource || !!(bankUpload && bankUpload.result.ocr_failed);
  const continuityWarn = !!continuityStmt;

  // Step 2 checklist sources
  const flaggedInvoice = invoices.find(i => i.needs_review_flags.length > 0);

  // Warning badges: steps 1–2 from backend warnings; step 5 from a
  // client-synthesized temp_nonzero (any temp leg non-empty). The backend
  // never emits temp_nonzero (spec §5.4) — Task 12 renders the banner.
  // Steps 3–4 carry no badge (mockup warnCounts); their state shows on the
  // S3 stat cards / S4 discrepancies card instead.
  const warnings = data?.warnings || [];
  const emptyLeg: WorkflowTempLeg = { bank_txs: [], invoices: [] };
  const tempReceipt = data?.temp?.receipt || emptyLeg;
  const tempPayment = data?.temp?.payment || emptyLeg;
  // Nov-4 (item 3-E): keep the bank-only count next to the S3 table — the stat
  // card used to add invoice legs the table never shows, so the count could
  // never be reproduced from the list below. parkedCount (bank + invoices)
  // still feeds the step-5 badge where both kinds count.
  const parkedBankCount = (tempReceipt.bank_txs?.length || 0) + (tempPayment.bank_txs?.length || 0);
  const parkedInvoiceCount = (tempReceipt.invoices?.length || 0) + (tempPayment.invoices?.length || 0);
  const parkedCount = parkedBankCount + parkedInvoiceCount;
  const tempNonZero = parkedCount > 0;
  const badgeCounts = [
    warnings.filter(w => w.step === 1).length,
    warnings.filter(w => w.step === 2).length,
    0, // step 3 — no badge (mockup); counts live on the S3 stat cards
    0, // step 4 — no badge (mockup); discrepancies card carries the count
    tempNonZero ? 1 : 0,
  ];

  const stepLabels = [
    tr('Bank Statement', '月結單', '月结单'),
    tr('Invoices', '發票', '发票'),
    tr('Link & Reconcile', '配對', '配对'),
    tr('Summary', '總覽', '总览'),
    tr('Financial Statement', '財務報表', '财务报表'),
  ];

  // ── modal helpers ──
  const openWarn = (kind: string, targetId?: string, detail?: string) => setWarnModal({ kind, targetId, detail });
  // Unknown-kind guard: workflowWarningDef() returns undefined for kinds the
  // dictionary does not know — render the minimal fallback below, never crash.
  const warnDef = warnModal
    ? workflowWarningDef(warnModal.kind as WorkflowWarningKind, { targetId: warnModal.targetId, detail: warnModal.detail })
    : undefined;

  const stmtDetail = (s?: WorkflowStatement): string | undefined => {
    if (!s) return undefined;
    const period = `${s.statement_year}/${String(s.statement_month).padStart(2, '0')}`;
    return tr(
      `Account ${s.account_number} · ${period}`,
      `賬戶 ${s.account_number} · ${period}`,
      `账户 ${s.account_number} · ${period}`,
    );
  };
  const invDetail = (i: WorkflowInvoice): string =>
    tr(
      `Invoice ${i.invoice_number}${i.counterparty ? ` · ${i.counterparty}` : ''}`,
      `發票 ${i.invoice_number}${i.counterparty ? ` · ${i.counterparty}` : ''}`,
      `发票 ${i.invoice_number}${i.counterparty ? ` · ${i.counterparty}` : ''}`,
    );

  // S4 discrepancy rows reuse the S1/S2 modal plumbing; this builds the
  // instance-specific detail line per target type (gap kinds embed the gap
  // number in their def text via ctx.targetId already).
  const warnDetail = (w: WorkflowWarning): string | undefined => {
    if (w.target_type === 'bank_statement') return stmtDetail(statements.find(s => s.id === w.target_id));
    if (w.target_type === 'invoice') {
      const inv = invoices.find(i => i.id === w.target_id);
      return inv ? invDetail(inv) : undefined;
    }
    return undefined;
  };

  // S3 compact table rows — every bank leg currently parked in Temp (receipt
  // then payment), tagged with its leg for the direction column. Read-only
  // view (spec §6): matching itself happens on /bank-statements.
  const tempLegRows = [
    ...tempReceipt.bank_txs.map(t => ({ ...t, leg: 'receipt' as const })),
    ...tempPayment.bank_txs.map(t => ({ ...t, leg: 'payment' as const })),
  ];

  const openOcrWarn = () => {
    const kind = ocrFlagStmt?.review_flags.find(f => f === 'ocr_failed' || f === 'balance_mismatch')
      ?? ocrFlagStmt?.review_flags.find(f => !isContinuityFlag(f))
      ?? needsReviewStmt?.review_flags.find(f => !isContinuityFlag(f))
      ?? 'ocr_failed';
    openWarn(kind, ocrStmtSource?.id ?? bankUpload?.result.review_id, stmtDetail(ocrStmtSource));
  };
  const openContinuityWarn = () => {
    const flag = continuityStmt?.review_flags.find(isContinuityFlag);
    if (flag) openWarn(flag, continuityStmt?.id, stmtDetail(continuityStmt));
  };
  const openInvoiceWarn = () => {
    const flag = flaggedInvoice?.needs_review_flags[0];
    if (flag && flaggedInvoice) openWarn(`invoice_${flag}`, flaggedInvoice.id, invDetail(flaggedInvoice));
  };

  // Supporting-document coverage (Nov 3, 2026-10-30): /dashboard/link-stats
  // was computed but fetched by no page — surface it on the matching step,
  // where "enough supporting documents" is actually judged.
  const { data: linkStats } = useQuery({
    queryKey: ['link-stats'],
    queryFn: () => api('/dashboard/link-stats') as Promise<any>,
    staleTime: 0,
  });

  const step1WarnAction = continuityWarn ? openContinuityWarn : (ocrWarn ? openOcrWarn : null);
  const step2WarnAction = flaggedInvoice ? openInvoiceWarn
    : (openGaps.length > 0 ? () => setGapModal(openGaps[0].gap_number) : null);

  // ── uploads ──
  async function runUpload(file: File, channel: WorkflowUploadChannel) {
    setBusyChannel(channel);
    try {
      const result = await uploadWorkflowDocument(file, channel);
      if (result.still_processing) {
        // 202-poll deadline hit with OCR still running — no import result yet.
        // The 15s summary refetch picks the draft up; say so instead of a
        // misleading "Imported".
        await queryClient.invalidateQueries({ queryKey: ['workflow-summary'] });
        toast.info(tr(
          'Still processing — check back in a few minutes; this page updates automatically.',
          '仍在處理中——請稍後回來查看；本頁會自動更新。',
          '仍在处理中——请稍后回来查看；本页会自动更新。',
        ), 7000);
        return;
      }
      setLastUpload({ channel, fileName: file.name, result });
      await queryClient.invalidateQueries({ queryKey: ['workflow-summary'] });
      if (result.ocr_failed) {
        toast.warning(tr(
          'OCR could not read this file — a draft was saved for manual review.',
          'OCR 無法讀取此文件——已存為草稿待人手覆核。',
          'OCR 无法读取此文件——已存为草稿待人手复核。',
        ), 6000);
      } else if (result.needs_review && result.review_id) {
        toast.info(tr('Imported — flagged for review.', '已匯入——已標記待覆核。', '已导入——已标记待复核。'));
      } else {
        toast.success(tr('Imported.', '已匯入。', '已导入。'));
      }
    } catch (e: any) {
      if (e instanceof WorkflowEncryptedPdfError) {
        // Ask for the password right here — the file is already uploaded, so
        // EncryptedPdfModal's try-decrypt can unlock + re-import it. Cancel
        // (or 5 wrong attempts) keeps the file stored with an unlock button
        // on its /file-storage row.
        setEncryptedPdf({ fileId: e.fileId, fileName: file.name });
        return;
      }
      toast.error(e?.message || tr('Upload failed.', '上載失敗。', '上传失败。'), 6000);
    } finally {
      setBusyChannel(null);
    }
  }

  function handleFile(file: File, channel: WorkflowUploadChannel) {
    const { accepted, rejected } = validateFiles([file]);
    if (rejected.length > 0) {
      toast.error(tr(
        'This file cannot be uploaded. Use PDF, PNG, JPG, WEBP or GIF (max 10 MB).',
        '此文件無法上載。請使用 PDF、PNG、JPG、WEBP 或 GIF（上限 10 MB）。',
        '此文件无法上传。请使用 PDF、PNG、JPG、WEBP 或 GIF（上限 10 MB）。',
      ), 6000);
      return;
    }
    if (accepted[0]) void runUpload(accepted[0], channel);
  }

  // ── gap reason (POST /invoices/number-gaps/:n/reason) ──
  async function saveGapReason(gapNumber: string, reasonCode: GapReasonCode, note: string) {
    try {
      await api(`/invoices/number-gaps/${encodeURIComponent(gapNumber)}/reason`, {
        method: 'POST',
        body: { reason_code: reasonCode, note: note || undefined },
      });
      setGapModal(null);
      await queryClient.invalidateQueries({ queryKey: ['workflow-summary'] });
      toast.success(tr('Reason saved.', '原因已儲存。', '原因已保存。'));
    } catch (e: any) {
      toast.error(e?.message || tr('Could not save the reason.', '無法儲存原因。', '无法保存原因。'), 6000);
    }
  }

  const importedNote = (fileName: string, r: WorkflowUploadResult) =>
    (r.needs_review && r.review_id)
      ? tr(`${fileName} imported — flagged for review`, `${fileName} 已匯入——已標記待覆核`, `${fileName} 已导入——已标记待复核`)
      : tr(`${fileName} imported`, `${fileName} 已匯入`, `${fileName} 已导入`);

  // S5 footer export — per active tab. The P&L reuses the exact export behind
  // the embedded Bookkeeping's "Export Excel (breakdown)" button (detail
  // endpoint → exportPlXlsx); BS/TB build single-sheet workbooks from the same
  // endpoints the embed reads (confirmed-postings view, server-default as-of),
  // so the Excel matches the on-screen default statement.
  async function exportActiveTab() {
    if (fsExporting) return;
    setFsExporting(true);
    try {
      const tenant = safeFilePart(user?.company_name || user?.name);
      const fyPart = `FY${selectedFY}`;
      if (fsTab === 'pl') {
        const data = await api(`/bookkeeping/income-statement/detail?start_date=${startDate}&end_date=${endDate}`);
        exportPlXlsx(data);
      } else if (fsTab === 'bs') {
        const bs = await api('/bookkeeping/balance-sheet');
        exportStatementExcel('bs', bsExportRows(bs), {
          title: `${tr('Balance Sheet', '資產負債表', '资产负债表')} — ${tr('as of', '截至', '截至')} ${bs.as_of || ''} (${tr('Confirmed postings', '已確認過賬', '已确认过账')})`,
          filename: `${tenant ? tenant + '_' : ''}${fyPart}_balance-sheet`,
        });
      } else {
        const tb = await api('/bookkeeping/trial-balance');
        exportStatementExcel('trial', trialExportRows(tb), {
          title: `${tr('Trial Balance', '試算表', '试算表')} — ${tr('as of', '截至', '截至')} ${tb.as_of || ''} (${tr('Confirmed postings', '已確認過賬', '已确认过账')})`,
          filename: `${tenant ? tenant + '_' : ''}${fyPart}_trial-balance`,
        });
      }
      toast.success(tr('Excel downloaded.', 'Excel 已下載。', 'Excel 已下载。'));
    } catch (e: any) {
      toast.error(e?.message || tr('Export failed.', '導出失敗。', '导出失败。'), 6000);
    } finally {
      setFsExporting(false);
    }
  }

  // 2026-10-28: a CLEAN import produces zero flagged statements (dormant
  // balance-neutral gaps don't flag — statement-continuity.ts 2026-10-27
  // ruling), which used to hide the check cards entirely and leave Step 1 a
  // blank dropzone after a successful upload. total_statements (server count)
  // keeps the cards up — green OKs — whenever the tenant has statements.
  const showStep1Checks = statements.length > 0 || (data?.step1?.total_statements ?? 0) > 0 || !!bankUpload;
  const showStep2Checks = invoices.length > 0 || gaps.length > 0
    || !!(lastUpload && (lastUpload.channel === 'sales_invoice' || lastUpload.channel === 'purchase_invoice'));
  // Item 3-C (Nov-4 consistency): this list keys on needs_review OR review_flags,
  // so reviewed statements still carrying flags count here while the review
  // queue (needs_review only) doesn't list them — scope difference, stated
  // instead of silently disagreeing.
  const step1ReviewedWithFlags = statements.filter((s: any) => s.status !== 'needs_review').length;

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold">{tr('Document Processing', '文件處理', '文件处理')}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {tr(
            'Upload statements → upload invoices → link & reconcile → summary → financial statements.',
            '上載月結單 → 上載發票 → 配對 → 總覽 → 財務報表。',
            '上传月结单 → 上传发票 → 配对 → 总览 → 财务报表。',
          )}
        </p>
        {data?.degraded?.length ? (
          <p className="text-xs text-muted-foreground mt-1">
            {tr(
              'Some sections could not be loaded — showing partial data.',
              '部分資料無法載入——顯示局部資料。',
              '部分资料无法载入——显示局部数据。',
            )}
          </p>
        ) : null}
        {/* Non-401 fetch failure with no data yet: without this the S4 empty
            state would celebrate "No outstanding discrepancies 🎉" on top of a
            dead fetch. The 15s refetch keeps retrying, so say just that. */}
        {error && !data ? (
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {tr(
              'Could not load workflow summary — retrying…',
              '無法載入流程總覽——重試中…',
              '无法载入流程总览——重试中…',
            )}
          </p>
        ) : null}
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-5 gap-2 mb-6">
        {stepLabels.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const count = badgeCounts[i];
          return (
            <button
              key={n}
              onClick={() => goStep(n)}
              className={`border rounded-lg p-3 text-left transition ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'}`}
            >
              <div className="flex items-center gap-2 text-xs font-medium">
                <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] shrink-0 ${active ? 'bg-white/20' : 'bg-muted'}`}>{n}</span>
                <span className="leading-tight">{label}</span>
                {count > 0 && (
                  <span className={`ml-auto flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${active ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`}>
                    <AlertTriangle className="h-3 w-3" />{count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Step panels — S1/S2 upload + checklists (Task 10), S3 matching entry
          + S4 summary (Task 11), S5 statements embed (Task 12). */}
      {step === 1 && (
        <div>
          <div className="bg-card border rounded-lg p-5 mb-4">
            <h3 className="font-semibold mb-1">
              {tr('Step 1 · Upload Bank Statement', '步驟 1 · 上載銀行月結單', '步骤 1 · 上传银行月结单')}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {tr(
                'PDF or image. OCR runs automatically; amounts and balances are checked.',
                '上載 PDF 或圖片，系統自動 OCR 並核對金額與餘額。',
                '上传 PDF 或图片，系统自动 OCR 并核对金额与余额。',
              )}
            </p>
            <DropZone
              icon={FileUp}
              title={tr('Click or drop a bank statement to upload', '點擊或拖放月結單上載', '点击或拖放月结单上传')}
              hint={tr('PDF · PNG · JPG (max 10 MB)', 'PDF · PNG · JPG（上限 10 MB）', 'PDF · PNG · JPG（上限 10 MB）')}
              busy={uploadInFlight}
              onFile={f => handleFile(f, 'bank_statement')}
            />
            {uploadInFlight && (
              <UploadProgress
                label={activeJobImporting
                  ? tr('Building statements…', '建立報表中…', '建立报表中…')
                  : tr('Uploading · OCR in progress…', '上載中 · OCR 處理中…', '上传中 · OCR 处理中…')}
              />
            )}
          </div>

          {showStep1Checks && (
            <div className="space-y-3">
              <CheckCard
                icon={ocrWarn ? AlertTriangle : CheckCircle2}
                warn={ocrWarn}
                pending={uploadInFlight}
                title={tr('OCR read', 'OCR 讀取', 'OCR 读取')}
                desc={ocrWarn
                  ? tr(
                      'OCR could not read a statement, or imported totals do not reconcile — review required.',
                      'OCR 無法讀取月結單，或匯入金額不符——需要覆核。',
                      'OCR 无法读取月结单，或导入金额不符——需要复核。',
                    )
                  : tr(
                      'OCR completed; amounts and balances reconcile.',
                      'OCR 完成，金額與餘額核對無誤。',
                      'OCR 完成，金额与余额核对无误。',
                    )}
                onClick={openOcrWarn}
              />
              <CheckCard
                icon={continuityWarn ? AlertTriangle : CheckCircle2}
                warn={continuityWarn}
                pending={uploadInFlight}
                title={tr('Statement continuity', '月結單連續性', '月结单连续性')}
                desc={continuityWarn
                  ? tr(
                      'A statement is missing, duplicated, or its balance chain is broken.',
                      '缺少、重複月結單，或餘額鏈斷裂。',
                      '缺少、重复月结单，或余额链断裂。',
                    )
                  : tr(
                      'Statement months and balances form an unbroken chain.',
                      '月結單月份與餘額連續無斷。',
                      '月结单月份与余额连续无断。',
                    )}
                onClick={openContinuityWarn}
              />
              <CheckCard
                icon={CheckCircle2}
                warn={false}
                pending={uploadInFlight}
                title={tr('Posting rule', '過賬規則', '过账规则')}
                desc={tr(
                  'Incoming cash: Dr Bank 11102 / Cr Temporary Receipt 11901 — linked items move to AR 11201',
                  '收入款項：借 銀行 11102 ／ 貸 暫收款 11901 —— 配對後自動轉入應收賬 11201',
                  '收入款项：借 银行 11102 ／ 贷 暂收款 11901 —— 配对后自动转入应收账 11201',
                )}
              />
              {bankUpload && (
                <div className="bg-muted/50 border rounded-lg p-3 text-xs text-muted-foreground flex items-center gap-1.5">
                  <FileCheck2 className="h-3.5 w-3.5 shrink-0" />
                  {importedNote(bankUpload.fileName, bankUpload.result)}
                </div>
              )}
              {step1ReviewedWithFlags > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {tr(
                    `${step1ReviewedWithFlags} of the flagged statement(s) above are already confirmed but still carry flags — they stay listed until the chain is confirmed; Pending Review lists only the ones awaiting action.`,
                    `以上 ${step1ReviewedWithFlags} 張已標記的月結單已完成覆核但仍帶有標記——鏈條確認前會继续顯示；待審核清單只列出待處理的項目。`,
                    `以上 ${step1ReviewedWithFlags} 张已标记的月结单已完成复核但仍带有标记——链条确认前会继续显示；待审核清单只列出待处理的项目。`,
                  )}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between mt-5">
            {step1WarnAction ? (
              <button onClick={step1WarnAction} className="px-3 py-2 text-sm border rounded-md hover:bg-muted flex items-center gap-1.5">
                <Pencil className="h-4 w-4" /> {tr('Fix warnings', '修正警告', '修正警告')}
              </button>
            ) : <span />}
            <button onClick={() => goStep(2)} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 flex items-center gap-1.5">
              {tr('Next: Invoices', '下一步：發票', '下一步：发票')} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="bg-card border rounded-lg p-5 mb-4">
            <h3 className="font-semibold mb-1">
              {tr('Step 2 · Upload Invoices', '步驟 2 · 上載發票', '步骤 2 · 上传发票')}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {tr(
                'Sales & purchase invoices; OCR, duplicate check and number-continuity run automatically.',
                '銷售及採購發票；系統自動 OCR、查重及檢查編號連續性。',
                '销售及采购发票；系统自动 OCR、查重及检查编号连续性。',
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DropZone
                icon={Files}
                title={tr('Sales invoices — click or drop to upload', '銷售發票——點擊或拖放上載', '销售发票——点击或拖放上传')}
                hint={tr('PDF · PNG · JPG (max 10 MB)', 'PDF · PNG · JPG（上限 10 MB）', 'PDF · PNG · JPG（上限 10 MB）')}
                busy={busyChannel === 'sales_invoice'}
                onFile={f => handleFile(f, 'sales_invoice')}
              />
              <DropZone
                icon={Files}
                title={tr('Purchase invoices — click or drop to upload', '採購發票——點擊或拖放上載', '采购发票——点击或拖放上传')}
                hint={tr('PDF · PNG · JPG (max 10 MB)', 'PDF · PNG · JPG（上限 10 MB）', 'PDF · PNG · JPG（上限 10 MB）')}
                busy={busyChannel === 'purchase_invoice'}
                onFile={f => handleFile(f, 'purchase_invoice')}
              />
            </div>
            {(busyChannel === 'sales_invoice' || busyChannel === 'purchase_invoice') && (
              <UploadProgress label={tr('Importing invoices · OCR + duplicate check…', '匯入發票 · OCR 及查重中…', '导入发票 · OCR 及查重中…')} />
            )}
          </div>

          {showStep2Checks && (
            <div className="space-y-3">
              <CheckCard
                icon={flaggedInvoice ? AlertTriangle : CheckCircle2}
                warn={!!flaggedInvoice}
                title={tr('OCR read', 'OCR 讀取', 'OCR 读取')}
                desc={flaggedInvoice
                  ? tr(
                      'At least one invoice is flagged for review (total mismatch, duplicate, counterparty, …).',
                      '至少一張發票被標記覆核（總額不符、重複、對家問題等）。',
                      '至少一张发票被标记复核（总额不符、重复、对家问题等）。',
                    )
                  : tr(
                      'All imported invoices passed OCR and duplicate checks.',
                      '所有匯入發票均通過 OCR 及查重檢查。',
                      '所有导入发票均通过 OCR 及查重检查。',
                    )}
                onClick={openInvoiceWarn}
              />
              <CheckCard
                icon={openGaps.length > 0 ? AlertTriangle : CheckCircle2}
                warn={openGaps.length > 0}
                title={openGaps.length > 0
                  ? tr('Skipped invoice numbers', '發票跳號', '发票跳号')
                  : tr('No skipped numbers', '無跳號', '无跳号')}
                desc={openGaps.length > 0
                  ? (
                    <div className="space-y-1">
                      {openGaps.map(g => (
                        <div key={g.gap_number} className="flex items-center gap-2 flex-wrap">
                          <span>
                            {tr(
                              `${g.gap_number} is missing from the number sequence`,
                              `發票編號 ${g.gap_number} 於編號序列中缺失`,
                              `发票编号 ${g.gap_number} 于编号序列中缺失`,
                            )}
                          </span>
                          <button onClick={() => setGapModal(g.gap_number)} className="underline text-primary">
                            {tr('Give reason', '說明原因', '说明原因')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                  : tr(
                      'Sales invoice numbers run without gaps.',
                      '銷售發票編號連續，沒有跳號。',
                      '销售发票编号连续，没有跳号。',
                    )}
                onClick={openGaps.length > 0 ? () => openWarn('invoice_number_gap', openGaps[0].gap_number) : undefined}
              />
              {gaps.filter(g => g.status === 'answered').length > 0 && (
                <div className="bg-muted/50 border rounded-lg p-3 text-xs text-muted-foreground flex items-start gap-1.5">
                  <FileCheck2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <div>
                    {tr('Answered gaps', '已說明原因的跳號', '已说明原因的跳号')}
                    {gaps.filter(g => g.status === 'answered').map(g => (
                      <div key={g.gap_number}>
                        {g.gap_number} — {gapReasonLabel(g.reason_code)}{g.note ? ` · ${g.note}` : ''}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <CheckCard
                icon={CheckCircle2}
                warn={false}
                title={tr('Posting rule', '過賬規則', '过账规则')}
                desc={tr(
                  'Sales: Dr Temporary Receipt 11901 (→ AR 11201 when linked) / Cr Revenue · Purchase: Dr Expenses / Cr Temporary Payment 21901 (→ AP 21101)',
                  '銷售：借 暫收款 11901（配對後轉應收賬 11201）／貸 收入 · 採購：借 費用 ／貸 暫付款 21901（配對後轉應付賬 21101）',
                  '销售：借 暂收款 11901（配对后转应收账 11201）／贷 收入 · 采购：借 费用 ／贷 暂付款 21901（配对后转应付账 21101）',
                )}
              />
              {lastUpload && (lastUpload.channel === 'sales_invoice' || lastUpload.channel === 'purchase_invoice') && (
                <div className="bg-muted/50 border rounded-lg p-3 text-xs text-muted-foreground flex items-center gap-1.5">
                  <FileCheck2 className="h-3.5 w-3.5 shrink-0" />
                  {importedNote(lastUpload.fileName, lastUpload.result)}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between mt-5">
            {step2WarnAction ? (
              <button onClick={step2WarnAction} className="px-3 py-2 text-sm border rounded-md hover:bg-muted flex items-center gap-1.5">
                <Pencil className="h-4 w-4" /> {tr('Fix warnings', '修正警告', '修正警告')}
              </button>
            ) : <span />}
            <button onClick={() => goStep(3)} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 flex items-center gap-1.5">
              {tr('Next: Link & Reconcile', '下一步：配對', '下一步：配对')} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — matching entry, read-only (spec §6): the matching engine
          stays on /bank-statements; the primary button deep-links its
          unmatched filter. The compact table lists the bank legs currently
          parked in Temp (continuity-flagged statements never generated draft
          JEs, so the lists may legitimately lag — render what's there). */}
      {step === 3 && (
        <div>
          <div className="bg-card border rounded-lg p-5 mb-4">
            <h3 className="font-semibold mb-1">
              {tr('Step 3 · Link Bank ↔ Invoices', '步驟 3 · 銀行配對', '步骤 3 · 银行配对')}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {tr(
                'Match bank transactions to invoices. Unlinked money automatically parks in Temporary Receipt / Temporary Payment.',
                '將銀行交易與發票配對；未配對款項自動暫記於暫收款／暫付款。',
                '将银行交易与发票配对；未配对款项自动暂记于暂收款／暂付款。',
              )}
            </p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="border rounded-md p-3 text-center">
                <div className="text-2xl font-bold">{data?.step3?.unmatched_bank_txs ?? 0}</div>
                <div className="text-xs text-muted-foreground">{tr('Unmatched bank txs', '未配對銀行交易', '未配对银行交易')}</div>
              </div>
              <div className="border rounded-md p-3 text-center">
                <div className="text-2xl font-bold">{data?.step3?.open_invoices ?? 0}</div>
                <div className="text-xs text-muted-foreground">{tr('Open invoices', '未收／付發票', '未收／付发票')}</div>
              </div>
              <div className="border rounded-md p-3 text-center">
                <div className={`text-2xl font-bold ${parkedBankCount > 0 ? 'text-red-600' : ''}`}>{parkedBankCount}</div>
                <div className="text-xs text-muted-foreground">{tr('Parked in Temp', '暫記中', '暂记中')}</div>
                {parkedInvoiceCount > 0 && (
                  <div className="text-[10px] mt-1 text-muted-foreground">
                    {tr(
                      `+${parkedInvoiceCount} invoice(s) held in temp (see Step 4)`,
                      `另有 ${parkedInvoiceCount} 張發票暫記（見步驟 4）`,
                      `另有 ${parkedInvoiceCount} 张发票暂记（见步骤 4）`,
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <CheckCard
                icon={FileCheck2}
                warn={!!linkStats && (
                  (linkStats.bank?.total > 0 && linkStats.bank.pct < 100)
                  || (linkStats.invoices?.total > 0 && linkStats.invoices.pct < 100)
                )}
                pending={!linkStats}
                title={tr('Supporting documents', '支持文件', '支持文件')}
                desc={linkStats ? (
                  <span className="font-mono text-[11px]">
                    {tr('Bank → invoice', '銀行→發票', '银行→发票')} {linkStats.bank?.pct ?? 0}% ({linkStats.bank?.linked ?? 0}/{linkStats.bank?.total ?? 0})
                    {' · '}
                    {tr('AP invoice → receipt', '應付發票→收據', '应付发票→收据')} {linkStats.invoices?.pct ?? 0}% ({linkStats.invoices?.linked_receipts ?? 0}/{linkStats.invoices?.total ?? 0})
                    {' · '}
                    {tr('Full chain', '完整鏈', '完整链')} {linkStats.full_chain?.pct ?? 0}%
                  </span>
                ) : tr('Checking supporting-document coverage…', '正在檢查支持文件覆蓋…', '正在检查支持文件覆盖…')}
                onClick={() => navigate('/bank-statements?filter=unmatched')}
              />
            </div>
            <button
              onClick={() => navigate('/bank-statements?filter=unmatched')}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-md hover:opacity-90 text-sm font-medium flex items-center justify-center gap-2"
            >
              <Link2 className="h-4 w-4" /> {tr('Open matching', '開始配對', '开始配对')}
            </button>
          </div>

          <div className="bg-card border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/60 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2.5">{tr('Date', '日期', '日期')}</th>
                  <th className="px-4 py-2.5">{tr('Description', '摘要', '摘要')}</th>
                  <th className="px-4 py-2.5">{tr('Temp', '暫記', '暂记')}</th>
                  <th className="px-4 py-2.5 text-right">{tr('Amount', '金額', '金额')}</th>
                  <th className="px-4 py-2.5">{tr('Status', '狀態', '状态')}</th>
                </tr>
              </thead>
              <tbody>
                {tempLegRows.length === 0 ? (
                  <tr className="border-t">
                    <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                      {tr('Nothing parked in Temp — no unmatched bank legs.', '暫記中沒有未配對的銀行交易。', '暂记中没有未配对的银行交易。')}
                    </td>
                  </tr>
                ) : tempLegRows.map(t => (
                  <tr key={t.id} className="border-t last:border-0">
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-4 py-2.5">{t.description || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">
                        {t.leg === 'receipt'
                          ? tr('Temp Receipt', '暫收款', '暂收款')
                          : tr('Temp Payment', '暫付款', '暂付款')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium whitespace-nowrap">{fmtAmt(t.amount)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                        {tr('Unmatched', '未配對', '未配对')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-5">
            <button onClick={() => goStep(4)} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 flex items-center gap-1.5">
              {tr('Next: Summary', '下一步：總覽', '下一步：总览')} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Summary: discrepancies card (one row per backend warning,
          click opens the shared warning modal) + Temp Receipt / Temp Payment
          tables (bank legs + invoices still held in temp) + the footnote. */}
      {step === 4 && (
        <div>
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="font-semibold text-lg">{tr('Workflow Summary', '流程總覽', '流程总览')}</h3>
            <span className="text-xs text-muted-foreground">
              {tr(
                'Also at /workflow/summary — reachable anytime',
                '亦可隨時經 /workflow/summary 直接前往',
                '亦可随时经 /workflow/summary 直接前往',
              )}
            </span>
          </div>

          <div className="bg-card border rounded-lg mb-4">
            <div className="px-4 py-3 border-b font-medium text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
              {tr('Missing items & discrepancies', '缺漏與差異', '缺漏与差异')}
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{warnings.length}</span>
            </div>
            {warnings.length > 0 ? warnings.map((w, i) => {
              // Folded amendment (Task 11 review F1): append the instance
              // detail (invoice number / statement period) to the row text so
              // duplicate warning kinds stay distinguishable in the list.
              const detail = warnDetail(w);
              const title = workflowWarningDef(w.kind as WorkflowWarningKind, { targetId: w.target_id, detail })?.title ?? w.kind;
              return (
              <div
                key={`${w.kind}-${w.target_id}-${i}`}
                onClick={() => openWarn(w.kind, w.target_id, detail)}
                className="px-4 py-3 border-b last:border-0 flex items-center gap-3 text-sm cursor-pointer hover:bg-muted/40"
              >
                <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 whitespace-nowrap">
                  {stepLabels[w.step - 1] || `S${w.step}`}
                </span>
                <span className="flex-1 min-w-0 truncate">
                  {title}{detail ? ` · ${detail}` : ''}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono shrink-0 hidden sm:inline">{w.kind}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </div>
              );
            }) : (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                {tr('No outstanding discrepancies 🎉', '無未處理差異 🎉', '无未处理差异 🎉')}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Temp Receipt — bank legs are signed; rows and subtotal show abs */}
            <div className="bg-card border rounded-lg">
              <div className="px-4 py-3 border-b font-medium text-sm flex items-center gap-2">
                <Inbox className="h-4 w-4 text-red-500 shrink-0" />
                <span className="truncate">{tr('Temp Receipt 11901', '暫收款 11901', '暂收款 11901')}</span>
                <span className="ml-auto text-xs font-semibold text-red-600 shrink-0">{fmtAmt(data?.temp?.receipt_total || 0)}</span>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {tempReceipt.bank_txs.length + tempReceipt.invoices.length === 0 ? (
                    <tr><td colSpan={3} className="px-3 py-5 text-center text-muted-foreground">{tr('Nothing parked here.', '此處沒有暫記項目。', '此处没有暂记项目。')}</td></tr>
                  ) : (
                    <>
                      {tempReceipt.bank_txs.map(t => (
                        <TempRow key={t.id} date={t.date} amount={t.amount}>{t.description || '—'}</TempRow>
                      ))}
                      {tempReceipt.invoices.map(inv => (
                        <TempRow key={inv.id} date={null} amount={inv.total}>
                          {`${inv.invoice_number}${inv.counterparty ? ` · ${inv.counterparty}` : ''} · ${tr('Sales invoice · unpaid (awaiting payment)', '銷售發票 · 未收款', '销售发票 · 未收款')}`}
                        </TempRow>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Temp Payment */}
            <div className="bg-card border rounded-lg">
              <div className="px-4 py-3 border-b font-medium text-sm flex items-center gap-2">
                <Send className="h-4 w-4 text-red-500 shrink-0" />
                <span className="truncate">{tr('Temp Payment 21901', '暫付款 21901', '暂付款 21901')}</span>
                <span className="ml-auto text-xs font-semibold text-red-600 shrink-0">{fmtAmt(data?.temp?.payment_total || 0)}</span>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {tempPayment.bank_txs.length + tempPayment.invoices.length === 0 ? (
                    <tr><td colSpan={3} className="px-3 py-5 text-center text-muted-foreground">{tr('Nothing parked here.', '此處沒有暫記項目。', '此处没有暂记项目。')}</td></tr>
                  ) : (
                    <>
                      {tempPayment.bank_txs.map(t => (
                        <TempRow key={t.id} date={t.date} amount={t.amount}>{t.description || '—'}</TempRow>
                      ))}
                      {tempPayment.invoices.map(inv => (
                        <TempRow key={inv.id} date={null} amount={inv.total}>
                          {`${inv.invoice_number}${inv.counterparty ? ` · ${inv.counterparty}` : ''} · ${tr('Purchase invoice · unpaid (awaiting payment)', '採購發票 · 未付款', '采购发票 · 未付款')}`}
                        </TempRow>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0" />
            {tr(
              'Items move out of Temp automatically once linked (→ AR 11201 / AP 21101).',
              '配對後自動由暫記轉入應收（11201）／應付（21101）。',
              '配对后自动由暂记转入应收（11201）／应付（21101）。',
            )}
          </p>

          <div className="flex justify-end mt-5">
            <button onClick={() => goStep(5)} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 flex items-center gap-1.5">
              {tr('Next: Financial Statement', '下一步：財務報表', '下一步：财务报表')} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 5 — Financial statements (Task 12): the embedded Bookkeeping is
          authoritative (its BS/TB account rows drill to the account
          transactions; the P&L formula banner expands COA breakdowns), driven
          by the header FY selector through DateFilterContext. The temp banner
          (controller ruling) triggers on ANY temp list being non-empty —
          never on summed totals — and opens the temp_nonzero warning modal.
          StatementDrillModal adds the voucher level per tab. */}
      {step === 5 && (
        <div>
          <div className="bg-card border rounded-lg p-5 mb-4">
            <h3 className="font-semibold mb-1">
              {tr('Step 5 · Financial Statement', '步驟 5 · 財務報表', '步骤 5 · 财务报表')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {tr(
                'Auto-built from posted entries for the period.',
                '由本期已過賬分錄自動生成。',
                '由本期已过账分录自动生成。',
              )}
            </p>
          </div>

          {tempNonZero && (
            <div
              onClick={() => openWarn('temp_nonzero')}
              className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3.5 flex items-start gap-3 text-sm cursor-pointer hover:shadow-sm"
            >
              <AlertTriangle className="h-5 w-5 mt-0.5 text-amber-500 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">
                  {tr('Temp accounts not cleared', '暫記科目未歸零', '暂记科目未归零')}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {tr(
                    'Finish linking in Step 3/4 before closing the period so parked money moves to AR/AP.',
                    '結賬前請於步驟 3／4 完成配對，將暫記款項轉入應收／應付。',
                    '结账前请于步骤 3／4 完成配对，将暂记款项转入应收／应付。',
                  )}
                </div>
              </div>
              <span className="text-xs text-muted-foreground mt-1 shrink-0">
                {tr('Click for details', '點擊查看詳情', '点击查看详情')}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div className="flex gap-1">
              {([
                ['pl', tr('Income Statement', '損益表', '损益表')],
                ['bs', tr('Balance Sheet', '資產負債表', '资产负债表')],
                ['trial', tr('Trial Balance', '試算表', '试算表')],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFsTab(id)}
                  className={`px-3 py-1.5 text-sm rounded-md transition ${fsTab === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setDrillOpen(true)}
              className="ml-auto px-3 py-1.5 text-sm border rounded-md hover:bg-muted flex items-center gap-1.5"
              title={tr('Pick an account and see its vouchers', '選擇科目查看其憑證', '选择科目查看其凭证')}
            >
              <BookOpen className="h-4 w-4" /> {tr('Vouchers', '憑證', '凭证')}
            </button>
          </div>

          <Bookkeeping initialTab={fsTab} hideTabs />

          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={exportActiveTab}
              disabled={fsExporting}
              className="px-3 py-2 text-sm border rounded-md hover:bg-muted flex items-center gap-1.5 disabled:opacity-60"
            >
              <FileSpreadsheet className="h-4 w-4 text-green-600" />
              {fsExporting
                ? tr('Preparing…', '準備中…', '准备中…')
                : tr('Export Excel (breakdown)', '導出 Excel（明細）', '导出 Excel（明细）')}
            </button>
            <button
              onClick={() => navigate('/income-statement')}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              {tr('Full report', '完整報表', '完整报表')}
            </button>
          </div>
        </div>
      )}

      {drillOpen && (
        <StatementDrillModal
          onClose={() => setDrillOpen(false)}
          tenantName={user?.company_name || user?.name}
        />
      )}

      {/* Warning detail modal — guarded against unknown kinds */}
      {warnModal && warnDef && (
        <WorkflowWarningModal
          kind={warnModal.kind as WorkflowWarningKind}
          targetId={warnModal.targetId}
          detail={warnModal.detail}
          onClose={() => setWarnModal(null)}
          onNavigate={to => { setWarnModal(null); navigate(to); }}
          onGapPrompt={() => {
            const n = warnModal.kind === 'invoice_number_gap' ? warnModal.targetId : openGaps[0]?.gap_number;
            setWarnModal(null);
            if (n) setGapModal(n);
          }}
        />
      )}
      {warnModal && !warnDef && (
        // Unknown warning kind — minimal fallback (never crash)
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setWarnModal(null)}>
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> {warnModal.kind}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {tr(
                'Unrecognised warning type — please refresh or contact support.',
                '無法識別的警告類型——請重新整理或聯絡支援。',
                '无法识别的警告类型——请刷新或联系支持。',
              )}
            </p>
            <div className="flex justify-end mt-4">
              <button onClick={() => setWarnModal(null)} className="px-4 py-2 text-sm border rounded-md hover:bg-muted">
                {tr('Close', '關閉', '关闭')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gap reason modal — wired to POST /invoices/number-gaps/:n/reason */}
      {gapModal && (
        <GapReasonModal
          gapNumber={gapModal}
          onSave={(reasonCode, note) => void saveGapReason(gapModal, reasonCode, note)}
          onSkip={() => setGapModal(null)}
          onClose={() => setGapModal(null)}
        />
      )}

      {/* Encrypted-PDF password prompt — cancel/blocked keeps the file stored
          (draft) with a later unlock path on File Storage. */}
      {encryptedPdf && (
        <EncryptedPdfModal
          fileId={encryptedPdf.fileId}
          fileName={encryptedPdf.fileName}
          onClose={() => {
            setEncryptedPdf(null);
            toast.info(tr(
              'File saved — you can enter its password later under File Storage.',
              '文件已保存——您之後可在「文件庫」輸入密碼解鎖。',
              '文件已保存——您之后可在「文件库」输入密码解锁。',
            ), 6000);
          }}
          onSuccess={() => {
            setEncryptedPdf(null);
            // try-decrypt already re-imported server-side — refresh the
            // wizard's step data so the new statement/invoice shows up.
            void queryClient.invalidateQueries({ queryKey: ['workflow-summary'] });
          }}
        />
      )}
    </div>
  );
}
